/**
 * Affiliate commerce — the brand's home for the creator affiliate programme.
 *
 * Three tabs in the order the work actually happens: list a product, set a
 * commission rule so creators can earn on it, then watch orders come in.
 *
 * The summary strip at the top splits commission into pending / approved /
 * paid rather than showing one total. They answer different questions --
 * pending is still at risk of being clawed back by a return, approved is owed,
 * paid is settled — and adding them together overstates the liability. The
 * accent colours say the same thing without reading the caption: amber for
 * money still at risk, green for money that is owed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Grid, Snackbar, Tab, Tabs } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ProductManagement from './ProductManagement';
import CommissionRules from './CommissionRules';
import AffiliateOrders from './AffiliateOrders';
import CreatorAccess from './CreatorAccess';
import CreatorDirectory from './CreatorDirectory';
import PageShell from '../../_helpers/reusableComponents/design/PageShell';
import KpiCard from '../../_helpers/reusableComponents/design/KpiCard';
import {
  palette,
  radius,
  shadow,
  motion,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

// The tab strip sits on the surface as a card of its own rather than as a rule
// drawn across the canvas, so the three views read as one switchable panel.
const TAB_STRIP_SX = {
  bgcolor: palette.surface,
  borderRadius: `${radius.md}px`,
  border: `1px solid ${palette.line}`,
  boxShadow: shadow.card,
  px: 1,
  mb: 3,
  minHeight: 52,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    backgroundColor: palette.brand,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 600,
    minHeight: 52,
    color: palette.muted,
    transition: `color ${motion.base}`,
    '&:hover': { color: palette.inkSoft },
  },
  '& .Mui-selected': { color: `${palette.brand} !important`, fontWeight: 700 },
};

const AffiliateCommerce = () => {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await CommerceService.getSummary();
      setSummary(data);
      setError('');
    } catch (err) {
      // A brand account that never finished setup gets a clear 403 from the
      // API. Surfacing it here beats every tab failing separately.
      setError(readApiError(err, 'Could not load commerce summary.'));
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, tab]);

  return (
    <PageShell
      icon={<StorefrontOutlinedIcon sx={{ fontSize: 28 }} />}
      badge="Affiliate programme"
      title="Affiliate Commerce"
      subtitle="List products, set commission, and track what creators earn for you."
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: `${radius.sm}px` }}>
          {error}
        </Alert>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<Inventory2OutlinedIcon />}
              label="Active products"
              value={summary.products.active}
              hint={`${summary.products.total} total`}
              accent={palette.brand}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<ReceiptLongOutlinedIcon />}
              label="Attributed orders"
              value={summary.orders.attributed}
              hint={`${summary.orders.total} orders, ${summary.orders.revenue} revenue`}
              accent={palette.info}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<HourglassEmptyOutlinedIcon />}
              label="Commission pending"
              value={summary.commission.pending}
              hint="Still within the return window"
              accent={palette.warning}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<AccountBalanceWalletOutlinedIcon />}
              label="Commission owed"
              value={summary.commission.approved}
              // Returns are named rather than silently netted off, so a
              // figure that dropped since yesterday reads as refunds and not
              // as a bug.
              hint={
                Number(summary.commission.reversed) > 0
                  ? `${summary.commission.paid} paid · ${summary.commission.reversed} returned`
                  : `${summary.commission.paid} already paid`
              }
              accent={palette.success}
            />
          </Grid>
        </Grid>
      )}

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={TAB_STRIP_SX}
      >
        <Tab icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Products" />
        <Tab icon={<PercentOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Commission rules" />
        <Tab icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Orders & earnings" />
        <Tab icon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Creator access" />
        <Tab icon={<PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Find creators" />
      </Tabs>

      {tab === 0 && <ProductManagement onNotify={notify} />}
      {tab === 1 && <CommissionRules onNotify={notify} />}
      {tab === 2 && <AffiliateOrders onNotify={notify} />}
      {tab === 3 && <CreatorAccess onNotify={notify} />}
      {tab === 4 && <CreatorDirectory onNotify={notify} />}

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: '100%', borderRadius: `${radius.sm}px`, boxShadow: shadow.modal }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default AffiliateCommerce;
